import { useState, useMemo } from 'react';
import toast from 'react-hot-toast';
import {
  useProviderCredentials,
  useDeleteProviderCredential,
} from '../services/providerCredentialsApi';
import type { ProviderCredential } from '../types/provider-credentials.types';
import { CredentialFormDialog } from '../components/CredentialFormDialog';

type DialogMode = null | 'create' | { edit: ProviderCredential };

export function ProviderCredentialsListView() {
  const { data: credentials, isLoading, error } = useProviderCredentials();
  const deleteMutation = useDeleteProviderCredential();

  const [dialogMode, setDialogMode] = useState<DialogMode>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [providerNameFilter, setProviderNameFilter] = useState('');

  const uniqueProviderNames = useMemo(() => {
    if (!credentials) return [];
    const names = [...new Set(credentials.map((c) => c.providerName))];
    return names.sort();
  }, [credentials]);

  const filteredCredentials = useMemo(() => {
    if (!credentials) return [];
    if (!providerNameFilter) return credentials;
    return credentials.filter((c) => c.providerName === providerNameFilter);
  }, [credentials, providerNameFilter]);

  const handleDelete = async (id: string) => {
    try {
      await deleteMutation.mutateAsync(id);
      toast.success('Credential deleted');
      setDeleteConfirmId(null);
    } catch {
      toast.error('Failed to delete credential');
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-gray-500">Loading credentials...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-3 bg-red-100 border border-red-400 text-red-700 rounded">
        Failed to load credentials
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Provider Credentials</h1>
        <button
          data-testid="add-credential-button"
          onClick={() => setDialogMode('create')}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
        >
          Add Credential
        </button>
      </div>

      {/* Provider Name Filter */}
      {uniqueProviderNames.length > 0 && (
        <div className="mb-4">
          <label className="text-sm font-medium text-gray-700 mr-2">Filter by provider:</label>
          <select
            data-testid="provider-name-filter"
            value={providerNameFilter}
            onChange={(e) => setProviderNameFilter(e.target.value)}
            className="px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All providers</option>
            {uniqueProviderNames.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Dialog */}
      {dialogMode === 'create' && (
        <CredentialFormDialog
          mode="create"
          onClose={() => setDialogMode(null)}
          onSuccess={() => {}}
        />
      )}
      {dialogMode !== null && typeof dialogMode === 'object' && 'edit' in dialogMode && (
        <CredentialFormDialog
          mode="edit"
          credential={dialogMode.edit}
          onClose={() => setDialogMode(null)}
          onSuccess={() => {}}
        />
      )}

      {/* Credentials Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Provider Type
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Provider Name
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Key Name
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Usage Type
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                API Key
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Priority
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Max Sessions
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Active
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredCredentials.length === 0 && (
              <tr>
                <td colSpan={9} className="px-6 py-8 text-center text-gray-500">
                  {providerNameFilter
                    ? 'No credentials match the filter'
                    : 'No credentials configured'}
                </td>
              </tr>
            )}
            {filteredCredentials.map((cred) => (
              <tr key={cred.id}>
                <td className="px-6 py-4 text-sm">
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      cred.providerType === 'stt'
                        ? 'bg-green-100 text-green-800'
                        : cred.providerType === 'tts'
                          ? 'bg-purple-100 text-purple-800'
                          : 'bg-yellow-100 text-yellow-800'
                    }`}
                  >
                    {cred.providerType.toUpperCase()}
                  </span>
                </td>
                <td className="px-6 py-4 text-sm text-gray-900">{cred.providerName}</td>
                <td className="px-6 py-4 text-sm text-gray-500">{cred.keyName}</td>
                <td className="px-6 py-4 text-sm">
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      cred.usageType === 'realtime'
                        ? 'bg-blue-100 text-blue-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}
                  >
                    {cred.usageType.charAt(0).toUpperCase() + cred.usageType.slice(1)}
                  </span>
                </td>
                <td className="px-6 py-4 text-sm font-mono text-gray-500">
                  {cred.credentials.api_key.length > 20
                    ? `${cred.credentials.api_key.substring(0, 20)}...`
                    : cred.credentials.api_key}
                </td>
                <td className="px-6 py-4 text-sm text-gray-500">{cred.priority}</td>
                <td className="px-6 py-4 text-sm text-gray-500">
                  {cred.maxConcurrentSessions ?? 'Unlimited'}
                </td>
                <td className="px-6 py-4">
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      cred.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}
                  >
                    {cred.isActive ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="px-6 py-4 space-x-2">
                  <button
                    data-testid={`edit-${cred.id}`}
                    onClick={() => setDialogMode({ edit: cred })}
                    className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                  >
                    Edit
                  </button>
                  {deleteConfirmId === cred.id ? (
                    <>
                      <button
                        data-testid={`confirm-delete-${cred.id}`}
                        onClick={() => handleDelete(cred.id)}
                        disabled={deleteMutation.isPending}
                        className="text-red-600 hover:text-red-800 text-sm font-medium disabled:opacity-50"
                      >
                        Confirm
                      </button>
                      <button
                        data-testid={`cancel-delete-${cred.id}`}
                        onClick={() => setDeleteConfirmId(null)}
                        className="text-gray-600 hover:text-gray-800 text-sm font-medium"
                      >
                        Cancel
                      </button>
                    </>
                  ) : (
                    <button
                      data-testid={`delete-${cred.id}`}
                      onClick={() => setDeleteConfirmId(cred.id)}
                      className="text-red-600 hover:text-red-800 text-sm font-medium"
                    >
                      Delete
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
